// reminders — reminder harian per-peran. Client-run saat login + dedup per hari.
// Cron server-side menyusul saat deploy. Port runReminders v0.18 (baris 542–553):
// overdue kerja→TEK, stale approval→CS, SLA breached→SPV, piutang lewat→FINANCE.
// (Cabang reminder absensi ditangani modul Absensi — butuh shift/holiday/leave.)
import { todayISO, ageDays } from '@domain/format';
import { pushNotif } from '@core/notif';
import { currentUser } from '@core/session';
import { supabase } from '@core/supabase';
import { kebijakan, absensi } from '@core/settings';
import { piutangTiket } from '@domain/money';
import { slaBreached, financeSlaInfo, overdueDays, fmtDurMin } from '@domain/sla';
import { toMinutes, isWorkday, shouldRemindMasuk } from '@domain/attendance';
import { listApprovals } from '@modules/audit/audit.repo';
import { listShifts, listOutletsGeo, listHolidays, myLeaves, todayAttendance } from '@modules/absensi/absensi.repo';
import { fetchPerluTickets, type PerluTicket } from './perlu.repo';

const rp = (n: number): string => 'Rp ' + (n || 0).toLocaleString('id-ID');
const sessionSent = new Set<string>(); // dedup dalam sesi (mirip flag lastRem* in-memory v0.18)

function overdueWork(t: PerluTicket): boolean {
  if (!['Diagnosa', 'Dikerjakan'].includes(t.status)) return false;
  if (!t.estSelesai) return false;
  const d = new Date(t.estSelesai);
  if (isNaN(d.getTime())) return false;
  return Date.now() > d.getTime() + 86400000;
}

// Klasifikasi jenis reminder dari teks (agar dedup HANYA cocok dgn reminder sejenis,
// bukan notif rantai next-actor bertarget sama). Signature = substring unik tiap reminder.
function reminderKind(text: string): string | null {
  if (text.includes('MELEWATI estimasi waktu')) return 'work';
  if (text.includes('menunggu persetujuan >1 hari')) return 'appr';
  if (text.includes('Verifikasi pembayaran') && text.includes('LEWAT SLA')) return 'verifsla';
  if (text.includes('Verifikasi refund') && text.includes('LEWAT SLA')) return 'refundsla';
  if (text.includes('Approval') && text.includes('LEWAT SLA')) return 'apprsla';
  if (text.includes('SLA LEWAT')) return 'sla';
  if (text.includes('jatuh tempo LEWAT')) return 'piutang';
  if (text.includes('Ingat absen masuk')) return 'absen';
  return null;
}

/** Kunci reminder yang SUDAH tercatat hari ini (dedup lintas-login) — best-effort. */
async function existingKeysToday(): Promise<Set<string>> {
  const s = new Set<string>();
  try {
    const startLocal = new Date(); startLocal.setHours(0, 0, 0, 0); // tengah malam LOKAL (bukan UTC)
    const { data } = await supabase().from('notifications')
      .select('target,outlet,ticket_no,text,created_at').gte('created_at', startLocal.toISOString());
    for (const r of (data ?? []) as { outlet: string | null; ticket_no: string | null; text: string }[]) {
      const kind = reminderKind(r.text || '');
      if (kind) s.add(kind + '|' + (r.outlet ?? '') + '|' + (r.ticket_no ?? ''));
    }
  } catch { /* dedup best-effort */ }
  return s;
}

// Mirror targetMatches (notif.view) — user hanya boleh READ notif yang cocok (RLS 0032).
// Kita HANYA push reminder yang target-nya bisa dibaca user login → pusher = audiens = bisa dedup
// (existingKeysToday hanya melihat notif yang boleh dibaca). Cegah duplikat lintas-peran/-login.
function canReadTarget(role: string, nama: string, id: number, target: string): boolean {
  if (role === 'Owner' || role === 'Manager') return true;
  if (target === 'EMP:' + id) return true; // reminder pribadi (selaras RLS 0040 / targetMatches)
  switch (role) {
    case 'SPV': return target === 'SPV' || target === 'CS' || target === 'FINANCE' || target.startsWith('TEK:');
    case 'CS': return target === 'CS';
    case 'Admin Finance': return target === 'FINANCE';
    case 'Teknisi': return target === 'TEK:' + nama;
    default: return false;
  }
}

export async function runReminders(): Promise<void> {
  const u = currentUser(); if (!u) return;
  const canRead = (target: string): boolean => canReadTarget(u.role, u.nama, u.id, target);
  let tickets: PerluTicket[];
  try { tickets = await fetchPerluTickets(); } catch { return; }
  const k = kebijakan();
  const today = todayISO();
  // Sparepart yang sudah diserahkan tapi tak dikonfirmasi teknisi dianggap diterima setelah
  // batas jam di Pengaturan → modal & hutang baru diakui di titik itu. Dijalankan di sini karena
  // aplikasi belum punya penjadwal di server: kalau tak ada yang membuka aplikasi, penandaannya
  // TERTUNDA (bukan hilang) sampai ada yang masuk. Supplier tak boleh menjalankannya (RPC menolak).
  if (u.role !== 'Supplier') {
    try { await supabase().rpc('auto_terima_sparepart', { p_jam: k.autoTerimaJam }); } catch { /* best-effort */ }
  }
  const seen = await existingKeysToday();

  // `tahap` = status tiket yang direminder → lonceng otomatis menghapus reminder ini saat tiket
  // MAJU dari status itu (walau tak diklik). Reminder tak-terikat-status (piutang agregat, SLA
  // verifikasi) tetap tanpa tahap — penyelesaiannya lewat resolveTaskNotif saat verifikasi.
  const push = async (kind: string, target: string, outlet: string | null, text: string, no: string, tahap?: string | null): Promise<void> => {
    if (!canRead(target)) return; // hanya push target yang bisa dibaca (agar bisa dedup) — cegah spam lintas-peran
    const key = kind + '|' + (outlet ?? '') + '|' + no;
    if (sessionSent.has(key) || seen.has(key)) return;
    sessionSent.add(key); seen.add(key);
    await pushNotif(target, outlet, text, no || undefined, tahap ?? null);
  };

  for (const t of tickets) {
    if (overdueWork(t) && t.teknisi)
      await push('work', 'TEK:' + t.teknisi, t.outlet, `⏰ Tiket ${t.no} (${t.unit}) MELEWATI estimasi waktu — segera selesaikan.`, t.no, t.status);
    if (t.status === 'Menunggu Persetujuan' && ageDays(t.tglISO) >= 1)
      await push('appr', 'CS', t.outlet, `⏳ Tiket ${t.no} menunggu persetujuan >1 hari — hubungi & konfirmasi pelanggan.`, t.no, t.status);
    if (k.slaOn && slaBreached(t.status, t.enterMs, t.estSelesai, k.slaOn, k.slaCfg))
      await push('sla', 'SPV', t.outlet, `⏰ SLA LEWAT — ${t.no} (${t.unit}) di status "${t.status}"${t.teknisi ? ' · ' + t.teknisi : ''} — segera tindak lanjut / eskalasi.`, t.no, t.status);
    // Eskalasi FINANCE (single-tier: FINANCE → juga terlihat atasan O/M via see-all).
    if (k.slaOn && t.ticket.lunas && !t.ticket.verified && !t.ticket.refund) {
      const s = financeSlaInfo(t.verifyStartMs, null, k.slaVerifikasiBayarMenit);
      if (s.applies && s.breached) await push('verifsla', 'FINANCE', t.outlet, `⚠️ Verifikasi pembayaran ${t.no} LEWAT SLA (lewat ${fmtDurMin(s.overMin ?? 0)}) — segera cek & verifikasi.`, t.no);
    }
    if (k.slaOn && t.ticket.refund && t.ticket.refund.status !== 'verified') {
      const s = financeSlaInfo(t.refundStartMs, null, k.slaVerifikasiRefundMenit);
      if (s.applies && s.breached) await push('refundsla', 'FINANCE', t.outlet, `⚠️ Verifikasi refund ${t.no} LEWAT SLA (lewat ${fmtDurMin(s.overMin ?? 0)}) — segera verifikasi.`, t.no);
    }
  }

  // Piutang → Finance (agregat per cabang, satu reminder/cabang/hari). Eskalasi saat LEWAT grace (slaPenagihanEskalasiHari) bila SLA aktif.
  // Gate FINANCE: hanya push bila user bisa baca FINANCE (agar bisa dedup lintas-login).
  if (canRead('FINANCE')) {
    const graceHari = k.slaOn ? k.slaPenagihanEskalasiHari : 0;
    const od = tickets.filter((t) => piutangTiket(t.ticket) > 0 && !!t.jatuhTempo && overdueDays(t.jatuhTempo, today) > graceHari);
    const byOut = new Map<string, { n: number; sum: number }>();
    for (const t of od) { const b = byOut.get(t.outlet) ?? { n: 0, sum: 0 }; b.n++; b.sum += piutangTiket(t.ticket); byOut.set(t.outlet, b); }
    for (const [outlet, b] of byOut) {
      const key = 'piutang|' + outlet + '|';
      if (sessionSent.has(key) || seen.has(key)) continue;
      sessionSent.add(key);
      await pushNotif('FINANCE', outlet, `📒 ${b.n} piutang jatuh tempo LEWAT (${rp(b.sum)}) — segera tagih pelanggan.`);
    }
  }

  // Eskalasi approval §26 lewat SLA → SPV (menjangkau SPV + Manager/Owner). Best-effort.
  // Gate SPV: skip fetch approvals bila user tak bisa baca SPV (cegah query + spam lintas-peran).
  if (k.slaOn && k.slaApprovalMenit > 0 && canRead('SPV')) {
    try {
      for (const a of await listApprovals('PENDING')) {
        const s = financeSlaInfo(new Date(a.at).getTime(), null, k.slaApprovalMenit);
        if (s.applies && s.breached)
          await push('apprsla', 'SPV', a.outlet, `⚠️ Approval ${a.module}/${a.ref_id} menunggu keputusan LEWAT SLA (lewat ${fmtDurMin(s.overMin ?? 0)}) — putuskan di Audit & Approval.`, a.ref_id);
      }
    } catch { /* eskalasi approval best-effort */ }
  }

  // Reminder masuk kerja (SELF, port v18 runReminders absensi): N menit sebelum shift bila belum absen.
  // Efektif self-only karena canRead('EMP:id') hanya true utk diri → user diingatkan saat login di window.
  // RESILIENT: data absensi (hari_kerja/holidays/leaves) butuh migrasi 0038/0039 — skip bila belum ada.
  if (u.outlet && canRead('EMP:' + u.id)) {
    try {
      const shifts = await listShifts(u.outlet);
      const att = shifts.length ? await todayAttendance(u.id, today) : null;
      if (shifts.length && !att) {
        const geo = (await listOutletsGeo()).find((o) => o.kode === u.outlet);
        const workday = geo ? isWorkday(geo.hari_kerja, today) : true;
        const hol = (await listHolidays()).some((h) => h.tgl === today && (!h.outlet || h.outlet === u.outlet));
        const onLeave = (await myLeaves(u.id)).some((l) => l.status === 'approved' && l.from_tgl <= today && today <= l.to_tgl);
        if (workday && !hol && !onLeave) {
          const earliest = shifts.map((s) => s.jam_masuk).sort()[0] ?? '';
          const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
          if (shouldRemindMasuk(nowMin, toMinutes(earliest), absensi().reminderMenit, false))
            await push('absen', 'EMP:' + u.id, u.outlet, `⏰ Ingat absen masuk — shift mulai ${earliest}. Jangan lupa clock-in.`, '');
        }
      }
    } catch { /* resilient: skip bila data absensi belum tersedia (migrasi) */ }
  }
}
